import {
  IresDevice,
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
  isActiveProgramChainOfIresDevice,
} from "@/data/models/IresDevice";
import { isHyDryMiniSerial } from "@/data/models/ComBox";
import {
  ProgramChain,
  getEndDateOfProgramChain,
  getProgramChainDurationDescription,
  getDryingPhaseInfoOfProgramChain,
  DryingPhaseInfo,
  findTimeRegionsOfInterestInProgramChain,
  getProgramChainDurationInMinutes,
  getProgramChainEndDate,
  ProgramChainTimeRegionOfInterest,
} from "@/data/models/ProgramChain";
import { MinMaxKeyValueAggregator } from "@/utils/SensorData";
import { calculatePausesDurationInMinutes } from "@/utils/DryingPauseCalculator";
import { ComBoxSensorType, ROOM_SENSOR_INDEX, TOTAL_SENSOR_COUNT } from "@/data/models/ComBoxSensorData";
import { roundWithPrecision } from "@/utils/Utils";
import { formatKWh, joinToString } from "@/utils/LocalisationHelper";
import { aggregateSensorSlotSerials, ComBox, getComBoxProgramName, calculateSlotNumberFromIndex, formatSensorSlotLabel, getSerialForSensorIndex } from "@/data/models/ComBox";
import Panel from "@/data/models/Panel";
import { ProjectAssignmentDetails } from "@/data/models/ProjectAssignmentInfo";
import { PanelConfig, getPanelProgramName } from "@/data/models/PanelConfig";
import { ComBoxSampleInfo, isConfigurationInfoSample, isConfigurationTypeInfoSample, isDryingPauseInfoSample, isUartStatusSample, PanelSampleInfo } from "@/data/models/SampleInfo";
import {
  ComBoxHumidityUnit,
  getComBoxHumidityUnitFormatRes,
  getHumidityValue,
} from "@/data/models/ComBoxHumidityUnit";

import {
  formatTimestampHoursMinutes,
  formatDateShort,
  formatDateHoursMinutes,
} from "@/utils/DateFormatter";
import {
  getNumberDaysBetween,
} from "@/utils/DateHelper";
import { formatProjectNameAndNumber } from "@/data/models/ProjectDetails";
import Vue from "vue";
import { getActiveUser } from "@/data/repository/UserRepository";
import { markEndDateManualCorrectionsAccess } from "@/permissions/UserPermissions";

export interface ProgramChainSensorInfo {
  dryingPhaseInfo: DryingPhaseInfo[];
  dryingPhaseLabels: string[];
  dryingDays: string[];
  sensorInfo: SensorInfo[];
}

export interface SensorInfo {
  key: string;

  sensorDescription?: string;
  /**
   * Optional secondary description rendered as an extra stacked label-only row
   * beneath the main row. Used for material (Widerstand) sensors — which have
   * only a single resistance row — to show the sensor-usage tag (e.g. "(MESS)")
   * the way regular sensors show it on their humidity row.
   */
  usageDescription?: string;
  label: string;

  minValue: string;
  maxValue: string;

  startValue: string;
  latestValue: string;

  phaseChangeInfo: string[];
}

interface CalculateDryingOptions {
  runAssignmentInfo?: string;
  showAssignetProjectName?: boolean;
  config?: PanelConfig;
}

export class DryingInfoCalculator {
  vue: Vue;
  iresDevice: IresDevice;
  programChain: ProgramChain;
  showRelativeChainNumber: boolean;
  options?: CalculateDryingOptions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any;

  constructor(
    vue: Vue,
    iresDevice: IresDevice,
    programChain: ProgramChain,
    showRelativeChainNumber: boolean,
    options?: CalculateDryingOptions
  ) {
    this.vue = vue;
    this.iresDevice = iresDevice;
    this.programChain = programChain;
    this.showRelativeChainNumber = showRelativeChainNumber;
    this.options = options;

    this.state = Vue.observable({
      programChainDurationInMinutes: 0,  
      programChainEndDate: new Date(),
      isLoading: true 
    });

    this.computeAdjustedProgramChainData().then(() => {
      this.state.isLoading = false;
    });
  }

  get serialNumber() {
    return this.iresDevice.serialNo;
  }

  get showSensorDescription() {
    return (
      getIresDeviceCategoryFromSerialNumber(this.serialNumber) ===
      IresDeviceCategory.COMBOX
    );
  }

  get latestValueHeader() {
    return this.vue
      .$t(
        this.isActiveProgramChain
          ? "dryingInfo.latestValue"
          : "dryingInfo.endValue"
      )
      .toString();
  }

  get firstRun() {
    return this.runs[0];
  }
  get lastRun() {
    return this.runs[this.runs.length - 1];
  }

  get isActiveProgramChain() {
    return isActiveProgramChainOfIresDevice(this.iresDevice, this.programChain);
  }

  get activeUser() {
    return getActiveUser();
  }

  get dryingDurationInfo(): string {
     if (this.state.isLoading) {
      return 'Loading...';
    }
    const startDate = this.firstRun.startDate;

    const formattedStartDate = formatTimestampHoursMinutes(startDate);
    const isActiveProgramChain = this.isActiveProgramChain;
    const timeRegionsOfInterest = this.state.timeRegionsOfInterest as ProgramChainTimeRegionOfInterest[];

    // Clamp pause/power-fail regions to the chain window, merge overlaps,
    // and sum to get total pause duration.
    const chainStartMs = startDate.toDate().getTime();
    const chainEndMs = this.isActiveProgramChain
      ? Date.now()
      : (this.state.programChainEndDate as Date).getTime();

    const pausesDurationInMinutes = calculatePausesDurationInMinutes(
      timeRegionsOfInterest,
      chainStartMs,
      chainEndMs
    );

    const programChainDurationInMinutes = this.state.programChainDurationInMinutes;

    const dryingPausePercentage =
      programChainDurationInMinutes > 0
        ? Math.round(
            (pausesDurationInMinutes / programChainDurationInMinutes) * 100
          )
        : 0;

    const programChainEndDate = this.state.programChainEndDate;
    const formattedDuration = this.state.programChainDurationDescription;

    const durationToDisplay =
      dryingPausePercentage === 0
        ? formattedDuration
        : this.vue.$t("dryingInfo.formatPercentageDryingPause", [
            formattedDuration,
            dryingPausePercentage,
          ]);

    if (isActiveProgramChain) {
      return this.vue
        .$t("dryingInfo.formatDurationOpenChain", [
          formattedStartDate,
          durationToDisplay,
        ])
        .toString();
    } else {
      const formattedEndDate_ = formatDateHoursMinutes(programChainEndDate);
      const formattedEndDate = markEndDateManualCorrectionsAccess(this.activeUser) && this.state.isEndDateAdjusted
        ? formattedEndDate_ + "*"
        : formattedEndDate_
      ;

      return this.vue
        .$t("dryingInfo.formatDurationCompletedChain", [
          formattedStartDate,
          formattedEndDate,
          durationToDisplay,
        ])
        .toString();
    }
  }

  // To temporarily fix the issue with the battery management for the panels (HDxxx series)
  async computeAdjustedProgramChainData() {
    const isActiveProgramChain = this.isActiveProgramChain;

    this.state.programChainDurationInMinutes = await getProgramChainDurationInMinutes(
      this.programChain,
      isActiveProgramChain
    );
    
    this.state.programChainEndDate = await getProgramChainEndDate(this.programChain);
    
    this.state.programChainDurationDescription = await getProgramChainDurationDescription(
      this.vue,
      this.programChain,
      isActiveProgramChain
    );

    const programChainDurationDescription = this.state.programChainDurationDescription;
    this.state.isEndDateAdjusted = 
      programChainDurationDescription[programChainDurationDescription.length - 1] === "*";

    this.state.programChainDurationDescription = this.state.isEndDateAdjusted
      ? programChainDurationDescription.slice (0, -1)
      : programChainDurationDescription
    ;

    this.state.timeRegionsOfInterest = await findTimeRegionsOfInterestInProgramChain(
      this.programChain
    );
  }

  getAssignmentDurationInfo(assignmentDetails: ProjectAssignmentDetails) {
    let prefix = "";

    if (this.options?.showAssignetProjectName) {
      prefix =
        this.vue
          .$t("pdfReport.formatProjectName", [
            joinToString(
              [assignmentDetails.projectNumber, assignmentDetails.projectName],
              ", "
            ),
          ])
          .toString() + " ";
    }

    const startDate = assignmentDetails.startDate;
    const endDate = assignmentDetails.endDate;

    const formattedStartDate = formatDateShort(startDate);
    if (endDate) {
      const formattedEndDate = formatDateShort(endDate);
      const numDays = getNumberDaysBetween(
        startDate.toDate(),
        endDate.toDate()
      );
      const formattedNumDays = this.vue
        .$tc("common.duration.formatDays", numDays, [numDays])
        .toString();

      return (
        prefix +
        this.vue.$t("dryingInfo.formatDurationClosedAssignment", [
          formattedStartDate,
          formattedEndDate,
          formattedNumDays,
        ])
      ).toString();
    } else {
      const numDays = getNumberDaysBetween(startDate.toDate(), new Date());
      const formattedNumDays = this.vue
        .$tc("common.duration.formatDays", numDays, [numDays])
        .toString();

      return (
        prefix +
        this.vue.$t("dryingInfo.formatDurationOpenAssignment", [
          formattedStartDate,
          formattedNumDays,
        ])
      );
    }
  }

  get programInfo() {
    const programNumber = this.runs[0]?.programNumber;

    if (programNumber === undefined) {
      return null;
    }

    const iresDevice = this.iresDevice;

    if (!iresDevice) {
      return null;
    }

    const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
      this.serialNumber
    );

    if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
      return getComBoxProgramName(
        this.vue,
        programNumber,
        iresDevice as ComBox
      );
    } else {
      return getPanelProgramName(
        this.vue,
        programNumber,
        iresDevice as Panel,
        this.options?.config
      );
    }
  }

  get meterReadingInfo(): string | null {
    if (!this.runs.length) {
      return null;
    }

    const initialMeterReading = this.firstRun.initialMeterReading;
    const latestMeterReading =
      this.lastRun.latestMeterReading ?? this.lastRun.initialMeterReading;

    if (initialMeterReading === undefined || latestMeterReading === undefined) {
      return null;
    }

    const powerConsumption = latestMeterReading - initialMeterReading;

    return this.vue
      .$t(
        `dryingInfo.${
          this.isActiveProgramChain
            ? "formatMeterReadingActiveRun"
            : "formatMeterReading"
        }`,
        [
          formatKWh(this.vue, roundWithPrecision(initialMeterReading, 1), 1),
          formatKWh(this.vue, roundWithPrecision(latestMeterReading, 1), 1),
          formatKWh(this.vue, roundWithPrecision(powerConsumption, 1), 1),
        ]
      )
      .toString();
  }

  get inventoryNumber() {
    return this.iresDevice.currentAssignment?.inventoryNumber ?? null;
  }

  get runs() {
    return this.programChain.runs;
  }

  getAssignedProjectDescription(
    assignmentDetails: ProjectAssignmentDetails
  ): string {
    if (!assignmentDetails) {
      return this.vue.$t("dryingInfo.noProject").toString();
    }

    return this.vue
      .$t("dryingInfo.project", [
        formatProjectNameAndNumber(
          assignmentDetails.projectNumber,
          assignmentDetails.projectName
        ),
      ])
      .toString();
  }

  get deviceDescription() {
    const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
      this.serialNumber
    );

    const formatKey = iresDeviceCategory === IresDeviceCategory.PANEL
      ? "formatHyDryPanel"
      : isHyDryMiniSerial(this.serialNumber)
        ? "formatHyDryMini"
        : "formatHyDryBox";

    return this.vue.$t(
      `iresDeviceType.${formatKey}`,
      [this.serialNumber, this.inventoryNumber]
    );
  }

  getChainNumber(programChainNumberLookup: Map<number, number>) {
    const absoluteChainNumber = this.programChain.chainNumber;
    const relativeChainNumber = programChainNumberLookup.get(
      this.programChain.chainNumber
    );

    if (this.showRelativeChainNumber) {
      return this.vue
        .$t("dryingInfo.formatRelativeAndAbsoluteChainNumber", [
          relativeChainNumber,
          absoluteChainNumber,
        ])
        .toString();
    }

    return this.vue.$t("panelDetail.dryingProgressGraphControl.formatDrying", [
      absoluteChainNumber,
    ]);
  }

  get showPanelInfo() {
    return (
      getIresDeviceCategoryFromSerialNumber(this.serialNumber) ===
      IresDeviceCategory.PANEL
    );
  }

  formatTemperature(temperature: number) {
    return this.vue
      .$t("common.formatTemperatureCompact", [
        temperature.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      ])
      .toString();
  }

  formatHumidity(humidityUnit: ComBoxHumidityUnit, value: number) {
    return this.vue
      .$t(getComBoxHumidityUnitFormatRes(humidityUnit), [
        value.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      ])
      .toString();
  }

  formatResistanceDigits(value: number) {
    return this.vue
      .$t("dryingGraph.formatResistanceDigits", [
        value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      ])
      .toString();
  }

  calculateSensorInfo(
    isAllowedToViewIntensity?: boolean,
    preferAbsHumidity?: boolean,
  ): ProgramChainSensorInfo {
    const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
      this.serialNumber
    );

    if (iresDeviceCategory === IresDeviceCategory.PANEL) {
      return this.calculatePanelSensorInfo(isAllowedToViewIntensity);
    } else {
      return this.calculateComBoxSensorInfo(preferAbsHumidity);
    }
  }

  private calculateDryingDays(dryingPhaseInfo: DryingPhaseInfo[]) {
    const startDate = this.firstRun.startDate;
    const programChainEndDate = this.state.programChainEndDate;

    const isEndDateAdjusted = 
      programChainEndDate.toString() !== getEndDateOfProgramChain(this.programChain).toDate().toString();

    const dryingDurationInDays = getNumberDaysBetween(
      startDate.toDate(),
      programChainEndDate
    );

    const dryingDurationInDaysString = isEndDateAdjusted && markEndDateManualCorrectionsAccess(this.activeUser)
      ? dryingDurationInDays.toString() + "*"
      : dryingDurationInDays.toString()

    return [
      "0",
      ...dryingPhaseInfo.map((value) => value.phaseEndDay.toString()),
      dryingDurationInDaysString,
    ];
  }

  //Might user later
  /* private formatEndOfPhase(phase: number) {
    switch (phase) {
      case 1:
        return this.vue.$t("comBoxDetail.formatEndOfPhases.boost").toString();
      case 2:
        return this.vue.$t("comBoxDetail.formatEndOfPhases.smart").toString();
      case 3:
        return this.vue.$t("comBoxDetail.formatEndOfPhases.comfort").toString();
      case 4:
        return this.vue.$t("comBoxDetail.formatEndOfPhases.eco").toString();
  
      default:
        return "";
    }
  } */

  calculateComBoxSensorInfo(preferAbsHumidity?: boolean) {
    const temperatureAggregators = Array.from(
      { length: TOTAL_SENSOR_COUNT },
      () => this.createMinMaxKeyValueAggregator()
    );

    const standardHumidityAggregators = Array.from(
      { length: TOTAL_SENSOR_COUNT },
      () => this.createMinMaxKeyValueAggregator()
    );

    const preferredHumidityAggregators = Array.from(
      { length: TOTAL_SENSOR_COUNT },
      () => this.createMinMaxKeyValueAggregator()
    );

    const resistanceDigitsAggregators = Array.from(
      { length: TOTAL_SENSOR_COUNT },
      () => this.createMinMaxKeyValueAggregator()
    );

    const sensorTypes = Array.from<ComBoxSensorType>(
      { length: TOTAL_SENSOR_COUNT },
    ).fill(ComBoxSensorType.UNKNOWN);

    let sensorUsages: string[] = [];

    const activeFallback = this.isActiveProgramChain
      ? (this.iresDevice as ComBox).comBoxStatus?.sensorSlotSerials
      : undefined;
    const sensorSlotSerials = aggregateSensorSlotSerials(this.runs, activeFallback);

    const standardHumidityUnit = ComBoxHumidityUnit.RELATIVE_HUMIDITY;
    const preferredHumidityUnit = preferAbsHumidity 
      ? ComBoxHumidityUnit.ABSOLUTE_HUMIDITY 
      : ComBoxHumidityUnit.MIXING_RATIO;
      
    const dryingPhaseInfo = getDryingPhaseInfoOfProgramChain(
      this.programChain,
      true,
      true,
    ).filter((info) => !info.isOpenEndedPhase);

    this.runs.forEach((run) => {
      const sensorIndices: number[] = [];

      run.sensorConfig.forEach((sensorType, index) => {
        if (
          sensorType !== ComBoxSensorType.EMPTY &&
          sensorType !== ComBoxSensorType.UNKNOWN
        ) {
          sensorIndices.push(index);

          if (sensorTypes[index] === ComBoxSensorType.UNKNOWN) {
            sensorTypes[index] = sensorType;
          }
        }
      });

      if (sensorUsages.length === 0) {
        sensorUsages = run.sensorUsage ?? [];
      }

      run.samples.forEach((sample) => {
        const comBoxSample = sample as ComBoxSampleInfo;
        
        const isUartStatus = isUartStatusSample(comBoxSample);
        const isDryingPauseInfo = isDryingPauseInfoSample(comBoxSample);
        const isConfigurationInfo = isConfigurationInfoSample(comBoxSample);
        const isConfigurationTypeInfo = isConfigurationTypeInfoSample(comBoxSample);
    
        const isSpecialEvent = 
          isUartStatus || 
          isDryingPauseInfo ||
          isConfigurationInfo ||
          isConfigurationTypeInfo;

        if (!isSpecialEvent) {
          sensorIndices.forEach((sensorIndex) => {
            const sensorData = comBoxSample.sensorData[sensorIndex];

            if (sensorData) {
              temperatureAggregators[sensorIndex]?.addValue(
                sensorData.temperature
              );
              standardHumidityAggregators[sensorIndex]?.addValue(
                getHumidityValue(standardHumidityUnit, sensorData)
              );
              preferredHumidityAggregators[sensorIndex]?.addValue(
                getHumidityValue(preferredHumidityUnit, sensorData)
              );
              resistanceDigitsAggregators[sensorIndex]?.addValue(
                sensorData.resistanceDigits
              );
            }
          });
        }
      });
    });

    const sensorInfo: SensorInfo[] = [];

    if (sensorTypes[ROOM_SENSOR_INDEX] !== ComBoxSensorType.UNKNOWN) {
      this.addSensorInfo(
        sensorInfo,
        "dryingInfo.temp",
        temperatureAggregators[ROOM_SENSOR_INDEX],
        this.getPhaseChangeSensorValue(
          dryingPhaseInfo,
          ROOM_SENSOR_INDEX,
          true,
          standardHumidityUnit
        ),
        this.vue.$t("iresDeviceSensor.roomSensor").toString(),
        (value) => this.formatTemperature(value)
      );

      this.addSensorInfo(
        sensorInfo,
        "dryingInfo.humidity",
        standardHumidityAggregators[ROOM_SENSOR_INDEX],
        this.getPhaseChangeSensorValue(
          dryingPhaseInfo,
          ROOM_SENSOR_INDEX,
          false,
          standardHumidityUnit
        ),
        this.formatSensorUsage(this.vue, sensorUsages[ROOM_SENSOR_INDEX]),
        (value) => this.formatHumidity(standardHumidityUnit, value)
      );

      this.addSensorInfo(
        sensorInfo,
        "",
        preferredHumidityAggregators[ROOM_SENSOR_INDEX],
        this.getPhaseChangeSensorValue(
          dryingPhaseInfo,
          ROOM_SENSOR_INDEX,
          false,
          preferredHumidityUnit
        ),
        undefined,
        (value) => this.formatHumidity(preferredHumidityUnit, value)
      );
    }

    for (let sensorIndex = 0; sensorIndex < TOTAL_SENSOR_COUNT; sensorIndex++) {
      if (sensorIndex === ROOM_SENSOR_INDEX) continue;

      if (sensorTypes[sensorIndex] !== ComBoxSensorType.UNKNOWN) {
        const sensorLabel = `Sensor${formatSensorSlotLabel(sensorIndex, sensorTypes[sensorIndex], getSerialForSensorIndex(sensorSlotSerials, sensorIndex), isHyDryMiniSerial(this.serialNumber)) ?? `${calculateSlotNumberFromIndex(sensorIndex)}L`}`;

        this.addSensorInfo(
          sensorInfo,
          "dryingInfo.temp",
          temperatureAggregators[sensorIndex],
          this.getPhaseChangeSensorValue(
            dryingPhaseInfo,
            sensorIndex,
            true,
            standardHumidityUnit
          ),
          sensorLabel,
          (value) => this.formatTemperature(value)
        );

        this.addSensorInfo(
          sensorInfo,
          "dryingInfo.humidity",
          standardHumidityAggregators[sensorIndex],
          this.getPhaseChangeSensorValue(
            dryingPhaseInfo,
            sensorIndex,
            false,
            standardHumidityUnit
          ),
          this.formatSensorUsage(this.vue, sensorUsages[sensorIndex]),
          (value) => this.formatHumidity(standardHumidityUnit, value)
        );

        this.addSensorInfo(
          sensorInfo,
          "",
          preferredHumidityAggregators[sensorIndex],
          this.getPhaseChangeSensorValue(
            dryingPhaseInfo,
            sensorIndex,
            false,
            preferredHumidityUnit
          ),
          undefined,
          (value) => this.formatHumidity(preferredHumidityUnit, value)
        );

        // Material sensors (BLE resistance) carry no temperature/humidity, so their
        // temp/humidity rows are skipped (empty aggregators -> createSensorInfo null).
        // The resistance-digits row therefore carries the sensor name so a material-only
        // sensor stays identifiable in the Messwerte table.
        this.addSensorInfo(
          sensorInfo,
          "dryingInfo.resistanceDigits",
          resistanceDigitsAggregators[sensorIndex],
          this.getPhaseChangeSensorValue(
            dryingPhaseInfo,
            sensorIndex,
            false,
            standardHumidityUnit,
            true
          ),
          sensorLabel,
          (value) => this.formatResistanceDigits(value),
          // Material (Widerstand) sensors are always measurement sensors and
          // have no temp/humidity row to carry the usage tag, so surface it
          // here as a stacked "(MESS)" line beneath the sensor name.
          this.formatSensorUsage(this.vue, "MEASUREMENT")
        );
      }
    }

    return {
      dryingPhaseInfo: dryingPhaseInfo,
      dryingPhaseLabels: dryingPhaseInfo.map((info) =>
        this.vue.$t("comBoxDetail.formatEndOfPhase", [info.phase]).toString()
      ),
      dryingDays: this.calculateDryingDays(dryingPhaseInfo),
      sensorInfo,
    };
  }

  private getPhaseChangeSensorValue(
    phaseChangeInfo: DryingPhaseInfo[],
    sensorIndex: number,
    getTemperature: boolean,
    comBoxHumidityUnit: ComBoxHumidityUnit,
    getResistanceDigits?: boolean
  ) {
    return phaseChangeInfo.map((info) => {
      const sensorValue = info.phaseEndSample.sensorData[sensorIndex];

      if (!sensorValue) return undefined;

      if (getResistanceDigits) return sensorValue.resistanceDigits;

      return getTemperature
        ? sensorValue.temperature
        : getHumidityValue(comBoxHumidityUnit, sensorValue);
    });
  }

  formatSensorUsageShort(vue: Vue, sensorUsage: string) {
    switch (sensorUsage) {
      case "PROGRAM":
        return vue.$t("comBoxDetail.sensorUsage.programShort").toString();
      case "MEASUREMENT":
        return vue.$t("comBoxDetail.sensorUsage.measurementShort").toString();
      case "REFERENCE":
        return vue.$t("comBoxDetail.sensorUsage.referenceShort").toString();
    }

    return null;
  }

  formatSensorUsage(vue: Vue, sensorUsage: string | undefined) {
    if (sensorUsage === undefined) {
      return undefined;
    }

    const formattedSensorUsage = this.formatSensorUsageShort(vue, sensorUsage);

    if (!formattedSensorUsage) {
      return undefined;
    }

    return `\u00A0\u00A0\u00A0\u00A0(${formattedSensorUsage})`;
  }

  createMinMaxKeyValueAggregator() {
    return new MinMaxKeyValueAggregator(
      (value) => value !== undefined && value !== 255,
      true
    );
  }

  calculatePanelSensorInfo(isAllowedToViewIntensity?: boolean): ProgramChainSensorInfo {
    const wallTempAggregator = this.createMinMaxKeyValueAggregator();
    const groundTempAggregator = this.createMinMaxKeyValueAggregator();
    const heatRatioAggregator = this.createMinMaxKeyValueAggregator();

    this.runs.forEach((run) => {
      run.samples
        .sort((s1, s2) => s1.sampleTimeInMinutes - s2.sampleTimeInMinutes)
        .forEach((sample) => {
          const panelSample = sample as PanelSampleInfo;

          wallTempAggregator.addValue(
            panelSample.maxWallTempStep ?? panelSample.wallTemp
          );

          if (panelSample.groundTemp !== undefined) {
            groundTempAggregator.addValue(
              panelSample.maxGroundTempStep ?? panelSample.groundTemp
            );
          }

          if (panelSample.heatRatioPercentage) {
            heatRatioAggregator.addValue(panelSample.heatRatioPercentage);
          }
        });
    });

    const sensorInfo: SensorInfo[] = [];

    this.addSensorInfo(sensorInfo, "dryingInfo.wallTemp", wallTempAggregator);
    this.addSensorInfo(
      sensorInfo,
      "dryingInfo.groundTemp",
      groundTempAggregator
    );

    if (isAllowedToViewIntensity) {
      this.addSensorInfo(
        sensorInfo,
        "dryingInfo.heatRatioWall",
        heatRatioAggregator
      );
    }

    return {
      dryingPhaseInfo: [],
      dryingPhaseLabels: [],
      dryingDays: this.calculateDryingDays([]),
      sensorInfo,
    };
  }

  addSensorInfo(
    collection: SensorInfo[],
    i18LabelRes: string,
    aggregator: MinMaxKeyValueAggregator,
    phaseChangeInfo?: (number | undefined)[],
    sensorDescription?: string,
    valueFormatter?: (value: number) => string,
    usageDescription?: string
  ) {
    const sensorInfo = this.createSensorInfo(
      i18LabelRes,
      aggregator,
      phaseChangeInfo,
      sensorDescription,
      valueFormatter,
      usageDescription
    );

    if (sensorInfo) {
      collection.push(sensorInfo);
    }
  }

  createSensorInfo(
    i18LabelRes: string,
    aggregator: MinMaxKeyValueAggregator,
    phaseChangeInfo?: (number | undefined)[],
    sensorDescription?: string,
    valueFormatter?: (value: number) => string,
    usageDescription?: string
  ): SensorInfo | null {
    const startValue = aggregator.firstValue;
    const endValue = aggregator.lastValue;

    if (startValue === null || endValue === null) {
      return null;
    }

    const formatter: (value: number) => string =
      valueFormatter ??
      ((value) =>
        value.toLocaleString(undefined, { maximumFractionDigits: 0 }));

    return {
      key: i18LabelRes,
      sensorDescription: sensorDescription,
      usageDescription: usageDescription,
      label: this.vue.$t(i18LabelRes).toString(),
      minValue: formatter(aggregator.min),
      maxValue: formatter(aggregator.max),
      startValue: formatter(startValue),
      latestValue: formatter(endValue),
      phaseChangeInfo:
        phaseChangeInfo?.map((value) =>
          value === undefined ? "" : formatter(value)
        ) ?? [],
    };
  }
}
